import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Phone, Mail, Landmark, HeartHandshake, Users, ShieldCheck, Clock, Check, Building2 } from "lucide-react";
import PageTransition from "../components/PageTransition";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { resolveImageUrl } from "../lib/imageUrl";

export default function Hajj() {
    const { settings, lang, formatPrice } = useApp();
    const [packages, setPackages] = useState([]);
    const waNum = (settings.whatsapp_number || "").replace(/\D/g, "");
    const waText = encodeURIComponent(lang === "en" ? "Hello, I would like information about your Hajj organization." : "Merhaba, Hac organizasyonunuz hakkında bilgi almak istiyorum.");

    useEffect(() => {
        api.get("/hajj-packages").then(({ data }) => setPackages(data || [])).catch(() => {});
    }, []);

    const highlights = [
        { icon: Landmark, title: lang === "en" ? "Sacred Journey" : "Kutsal Yolculuk", desc: lang === "en" ? "Complete Hajj organization with accommodation near the Haram" : "Harem'e yakın konaklamalı eksiksiz Hac organizasyonu" },
        { icon: Users, title: lang === "en" ? "Expert Guides" : "Uzman Rehberler", desc: lang === "en" ? "Scholarly guides accompany you at every step of worship" : "İbadetlerinizin her adımında ilim ehli hocalarımız yanınızda" },
        { icon: ShieldCheck, title: lang === "en" ? "Trusted Service" : "Güvenilir Hizmet", desc: lang === "en" ? "Years of experience in Makkah & Madinah" : "Mekke ve Medine'de yılların tecrübesi" },
    ];

    const pkgWa = (title) => {
        const txt = encodeURIComponent(lang === "en"
            ? `Hello, I would like information about the "${title}" Hajj package.`
            : `Merhaba, "${title}" Hac paketi hakkında bilgi almak istiyorum.`);
        return `https://wa.me/${waNum}?text=${txt}`;
    };

    return (
        <PageTransition>
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-testid="hajj-page">
                <div className="text-center mb-10">
                    <div className="text-xs uppercase tracking-[0.35em] text-gold mb-3">{lang === "en" ? "HAJJ ORGANIZATION" : "HAC ORGANİZASYONU"}</div>
                    <h1 className="font-serif text-4xl sm:text-5xl text-bone tracking-tight">{lang === "en" ? "Hajj" : "Hac"}</h1>
                    <p className="text-bone/70 mt-4 max-w-2xl mx-auto leading-relaxed">
                        {lang === "en"
                            ? "We organize your blessed Hajj journey with care — accommodation, transfers and guidance."
                            : "Mübarek Hac yolculuğunuzu konaklama, transfer ve rehberlik hizmetleriyle özenle organize ediyoruz."}
                    </p>
                </div>

                <div className="grid sm:grid-cols-3 gap-3 mb-10 max-w-4xl mx-auto">
                    {highlights.map(({ icon: Icon, title, desc }, i) => (
                        <motion.div
                            key={title}
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.08, duration: 0.4 }}
                            className="border border-border_green bg-emerald_surface p-5"
                        >
                            <Icon className="w-5 h-5 text-gold mb-3" />
                            <div className="font-serif text-lg mb-1">{title}</div>
                            <div className="text-xs text-muted_green leading-relaxed">{desc}</div>
                        </motion.div>
                    ))}
                </div>

                {packages.length > 0 && (
                    <div className="mb-12" data-testid="hajj-packages">
                        <div className="text-center mb-8">
                            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-2">{lang === "en" ? "OUR PACKAGES" : "PAKETLERİMİZ"}</div>
                            <h2 className="font-serif text-3xl sm:text-4xl">{lang === "en" ? "Hajj Packages" : "Hac Paketleri"}</h2>
                        </div>
                        <div className="grid md:grid-cols-2 gap-6">
                            {packages.map((p, i) => (
                                <motion.div
                                    key={p.id}
                                    initial={{ opacity: 0, y: 18 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.06, duration: 0.45 }}
                                    className="border border-border_green bg-emerald_surface flex flex-col overflow-hidden hover:border-gold/50 transition-colors"
                                    data-testid={`hajj-package-${p.id}`}
                                >
                                    {(p.images || [])[0] && (
                                        <div className="aspect-[16/9] overflow-hidden bg-emerald_deep">
                                            <img src={resolveImageUrl(p.images[0])} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                                        </div>
                                    )}
                                    <div className="p-6 flex flex-col flex-1">
                                        <h3 className="font-serif text-2xl mb-2">{p.title}</h3>
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {p.duration && (
                                                <span className="inline-flex items-center gap-1 border border-border_green px-2 py-1 text-[11px] text-muted_green">
                                                    <Clock className="w-3 h-3 text-gold" /> {p.duration}
                                                </span>
                                            )}
                                            {p.quota && (
                                                <span className="inline-flex items-center gap-1 border border-border_green px-2 py-1 text-[11px] text-muted_green">
                                                    <Users className="w-3 h-3 text-gold" /> {p.quota}
                                                </span>
                                            )}
                                        </div>
                                        {(p.mecca_hotel || p.medina_hotel) && (
                                            <div className="text-xs text-muted_green mb-3 space-y-1">
                                                {p.mecca_hotel && <div className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-gold flex-shrink-0" /> {lang === "en" ? "Makkah" : "Mekke"}: {p.mecca_hotel}</div>}
                                                {p.medina_hotel && <div className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-gold flex-shrink-0" /> {lang === "en" ? "Madinah" : "Medine"}: {p.medina_hotel}</div>}
                                            </div>
                                        )}
                                        {p.description && <p className="text-sm text-muted_green leading-relaxed mb-4 whitespace-pre-line">{p.description}</p>}
                                        {(p.features || []).length > 0 && (
                                            <div className="grid grid-cols-2 gap-1.5 mb-4">
                                                {p.features.map((f, fi) => (
                                                    <div key={fi} className="flex items-start gap-1.5 text-xs text-bone/85">
                                                        <Check className="w-3.5 h-3.5 text-gold flex-shrink-0 mt-0.5" /> {f}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div className="mt-auto pt-4 border-t border-border_green flex items-center justify-between gap-3 flex-wrap">
                                            <div>
                                                {Number(p.price) > 0 ? (
                                                    <>
                                                        <div className="font-serif text-2xl text-gold" data-testid={`hajj-price-${p.id}`}>{formatPrice(p.price)}</div>
                                                        {p.price_note && <div className="text-[10px] text-muted_green">{p.price_note}</div>}
                                                    </>
                                                ) : (
                                                    <div className="text-xs text-muted_green">{lang === "en" ? "Contact us for pricing" : "Fiyat için iletişime geçin"}</div>
                                                )}
                                            </div>
                                            {waNum ? (
                                                <a href={pkgWa(p.title)} target="_blank" rel="noreferrer" className="btn-gold !text-xs !px-5" data-testid={`hajj-info-${p.id}`}>
                                                    <MessageCircle className="w-3.5 h-3.5" /> {lang === "en" ? "Get Info" : "Bilgi Al"}
                                                </a>
                                            ) : (
                                                <Link to="/contact" className="btn-gold !text-xs !px-5" data-testid={`hajj-info-${p.id}`}>
                                                    <Mail className="w-3.5 h-3.5" /> {lang === "en" ? "Get Info" : "Bilgi Al"}
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="border border-gold/40 bg-emerald_surface p-8 sm:p-10 text-center max-w-4xl mx-auto" data-testid="hajj-contact-box">
                    <HeartHandshake className="w-8 h-8 text-gold mx-auto mb-4" />
                    <h2 className="font-serif text-2xl sm:text-3xl mb-3">
                        {lang === "en" ? "Contact us for more information" : "Bilgi almak için bizimle iletişime geçin"}
                    </h2>
                    <p className="text-sm text-muted_green max-w-xl mx-auto mb-7">
                        {lang === "en"
                            ? "Our team will inform you about Hajj quotas, dates, accommodation options and pricing."
                            : "Ekibimiz; Hac kontenjanları, tarihler, konaklama seçenekleri ve fiyatlandırma hakkında sizi bilgilendirecektir."}
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                        {waNum && (
                            <a
                                href={`https://wa.me/${waNum}?text=${waText}`}
                                target="_blank"
                                rel="noreferrer"
                                className="btn-gold !px-6"
                                data-testid="hajj-whatsapp-btn"
                            >
                                <MessageCircle className="w-4 h-4" /> WhatsApp
                            </a>
                        )}
                        {waNum && (
                            <a href={`tel:+${waNum}`} className="btn-outline-gold !px-6" data-testid="hajj-phone-btn">
                                <Phone className="w-4 h-4" /> {lang === "en" ? "Call Us" : "Bizi Arayın"}
                            </a>
                        )}
                        <Link to="/contact" className="btn-outline-gold !px-6" data-testid="hajj-contact-link">
                            <Mail className="w-4 h-4" /> {lang === "en" ? "Contact Form" : "İletişim Sayfası"}
                        </Link>
                    </div>
                </div>
            </section>
        </PageTransition>
    );
}
